import React from "react";
import { useTranslation } from "react-i18next";
import { Mail, ShieldAlert } from "lucide-react";

/**
 * Écran affiché quand un sondage « sur invitation » ne peut pas être voté.
 *
 * Remplace l'ancien EmailAuthGate, qui comparait l'adresse saisie à une liste
 * envoyée au navigateur : contournable, et la liste des invités fuitait vers
 * tout visiteur du lien. Ici, rien n'est décidé côté client — le motif vient
 * de la RPC `check_poll_invitation`, et c'est de toute façon
 * `submit_poll_response` qui tranche à l'écriture.
 *
 * Aucun champ email : proposer d'en saisir un permettrait de tester qui
 * figure dans la liste (énumération d'adresses).
 */

export type InviteGateReason =
  | "invite_required"
  | "invite_invalid"
  | "invite_revoked"
  | "invite_expired"
  | "invite_exhausted"
  | "invite_bound_elsewhere"
  | "invite_email_mismatch";

interface InviteGateProps {
  reason: InviteGateReason;
  pollTitle?: string;
  /** Affiché si l'invitation est connue : « vous votez en tant que m****e@… ». */
  emailMasked?: string | null;
  /** Proposé quand se connecter peut débloquer la situation. */
  onSignIn?: () => void;
}

export function InviteGate({
  reason,
  pollTitle,
  emailMasked,
  onSignIn,
}: InviteGateProps) {
  const { t } = useTranslation(["polls"]);

  // Se connecter ne résout que les cas « je n'ai pas / plus mon lien » : si
  // l'invitation est révoquée ou épuisée, aucun compte n'y changera rien.
  const signInHelps =
    reason === "invite_required" ||
    reason === "invite_invalid" ||
    reason === "invite_bound_elsewhere";

  return (
    <div className="flex flex-1 items-center justify-center p-4">
      <div
        className="w-full max-w-md rounded-2xl border border-border bg-card p-6 text-center"
        data-testid="invite-gate"
        data-reason={reason}
      >
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
          {reason === "invite_required" ? (
            <Mail className="h-6 w-6 text-primary" />
          ) : (
            <ShieldAlert className="h-6 w-6 text-warning" />
          )}
        </div>

        <h1 className="mb-2 text-lg font-semibold text-foreground">
          {t("polls:inviteGate.title")}
        </h1>

        {pollTitle && (
          <p className="mb-3 truncate text-sm text-muted-foreground">
            {pollTitle}
          </p>
        )}

        <p className="mb-5 text-sm text-foreground">
          {t(`polls:inviteGate.reason.${reason}`)}
        </p>

        {emailMasked && (
          <p className="mb-5 text-xs text-muted-foreground">
            {t("polls:inviteGate.identifiedAs", { email: emailMasked })}
          </p>
        )}

        <p className="mb-4 text-xs text-muted-foreground">
          {t("polls:inviteGate.askOrganizer")}
        </p>

        {signInHelps && onSignIn && (
          <button
            type="button"
            onClick={onSignIn}
            data-testid="invite-gate-signin"
            className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            {t("polls:inviteGate.signInCta")}
          </button>
        )}
      </div>
    </div>
  );
}

export default InviteGate;
